import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  applyReportDateToSearchParams,
  formatYmdLocal,
  parseReportDateSearchParams,
  type ReportDatePreset,
} from "@/lib/reportDateFilterUrl";
import {
  parseContactSegmentParam,
  type ContactSegment,
} from "@/lib/contactSegmentFilter";
import { instagramProfileUrlFromHandle } from "@/lib/instagramProfileUrl";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  MoreHorizontal,
} from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import type { DateRange as DayPickerRange } from "react-day-picker";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import PhoneInput from "react-phone-number-input";

interface Lead {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  instagram?: {
    igUsername?: string;
  };
  joinedAt?: string;
  sortDate?: string;
  /** Soft-deleted (e.g. merged duplicate); excluded from list API — filtered client-side if present. */
  deletedAt?: string | null;
  numPurchases?: number;
  /** Stripe smallest currency unit (e.g. cents for USD), same as Lead model. */
  amountSpent?: number;
  lastActive?: string;
  numEngagements?: number;
}

type ContactTileCounts = Record<ContactSegment, number>;

const CONTACT_TILES: { segment: ContactSegment; label: string }[] = [
  { segment: "superfans", label: "Superfans" },
  { segment: "supporters", label: "Supporters" },
  { segment: "active", label: "Active" },
  { segment: "contacts", label: "Contacts" },
];

interface LeadGroup {
  _id: string;
  name: string;
  contacts: number;
}

type ContactsSortColumn =
  | "name"
  | "instagram"
  | "email"
  | "phone"
  | "amountSpent"
  | "numPurchases"
  | "numEngagements"
  | "lastActive"
  | "joined";

function defaultSortDirForColumn(col: ContactsSortColumn): "asc" | "desc" {
  if (
    col === "amountSpent" ||
    col === "numPurchases" ||
    col === "numEngagements" ||
    col === "lastActive" ||
    col === "joined"
  ) {
    return "desc";
  }
  return "asc";
}

function leadsWithoutDeletedAt(leads: Lead[]): Lead[] {
  return leads.filter((l) => l.deletedAt == null);
}

export function Contacts() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const segment = useMemo(
    () => parseContactSegmentParam(searchParams.get("segment")),
    [searchParams],
  );
  const { preset, customRange } = useMemo(
    () => parseReportDateSearchParams(searchParams, { defaultPreset: "All" }),
    [searchParams],
  );
  const [tileCounts, setTileCounts] = useState<ContactTileCounts>({
    superfans: 0,
    supporters: 0,
    active: 0,
    contacts: 0,
  });
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tempDateRange, setTempDateRange] = useState<DayPickerRange | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [isAddToGroupDialogOpen, setIsAddToGroupDialogOpen] = useState(false);
  const [leadGroups, setLeadGroups] = useState<LeadGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");
  const [isAddingToGroup, setIsAddingToGroup] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [sortBy, setSortBy] = useState<ContactsSortColumn>("amountSpent");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [newLead, setNewLead] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  });
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const appendLeadsDateFilters = (params: URLSearchParams) => {
    const from = customRange?.from;
    const to = customRange?.to;
    if (from && to) {
      params.append("start", formatYmdLocal(from));
      params.append("end", formatYmdLocal(to));
    } else {
      params.append("dateRange", preset);
    }
  };

  const appendLeadsListFilters = (params: URLSearchParams) => {
    appendLeadsDateFilters(params);
    if (segment !== "contacts") {
      params.append("segment", segment);
    }
    params.append("sortBy", sortBy);
    params.append("sortDir", sortDir);
  };

  const handleSortColumn = (column: ContactsSortColumn) => {
    setCurrentPage(1);
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir(defaultSortDirForColumn(column));
    }
  };

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        appendLeadsListFilters(params);
        params.append("page", currentPage.toString());
        params.append("limit", pageSize.toString());
        const url = `/api/leads${params.toString() ? `?${params.toString()}` : ""}`;
        const response = await fetch(url);

        if (response.ok) {
          const data = await response.json();
          setLeads(leadsWithoutDeletedAt(data.leads || []));
          if (data.tileCounts) {
            setTileCounts(data.tileCounts);
          }
          if (data.pagination) {
            setTotalCount(data.pagination.totalCount || 0);
            setTotalPages(data.pagination.totalPages || 0);
          }
        } else {
          console.error("Failed to fetch leads");
        }

        setBreadcrumbs([
          { label: "Contacts", path: "/contacts" },
        ]);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();

    return () => {
      clearBreadcrumbs();
    };
  }, [segment, preset, customRange, currentPage, pageSize, sortBy, sortDir]);

  useEffect(() => {
    async function fetchLeadGroups() {
      try {
        const response = await fetch("/api/lead-groups");
        if (response.ok) {
          const data = await response.json();
          setLeadGroups(data.groups || []);
        }
      } catch (error) {
        console.error("Failed to fetch lead groups:", error);
      }
    }

    if (isAddToGroupDialogOpen) {
      fetchLeadGroups();
    }
  }, [isAddToGroupDialogOpen]);

  useEffect(() => {
    // Update select all state based on selected leads
    if (leads.length > 0) {
      const allSelected = leads.every((lead) => selectedLeads.has(lead._id));
      setIsSelectAll(allSelected);
    } else {
      setIsSelectAll(false);
    }
  }, [selectedLeads, leads]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getName = (lead: Lead) => {
    const parts = [lead.firstName, lead.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "-";
  };

  const formatPhoneNumber = (phoneNumber?: string) => {
    if (!phoneNumber) return "-";
    try {
      const phone = parsePhoneNumberFromString(phoneNumber);
      return phone ? phone.formatInternational() : phoneNumber;
    } catch (error) {
      // If parsing fails, return the original number
      return phoneNumber;
    }
  };

  const formatAmountSpent = (cents?: number) => {
    if (cents == null || !Number.isFinite(cents)) return "-";
    const dollars = cents / 100;
    return `$${dollars.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatNumPurchases = (n?: number) => {
    if (n == null || !Number.isFinite(n)) return "-";
    return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  const formatNumEngagements = (n?: number) => {
    if (n == null || !Number.isFinite(n)) return "-";
    return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  };

  const isAllDateFilter = preset === "All" && !customRange;

  const getContactTileLabel = (tileSegment: ContactSegment, baseLabel: string) => {
    if (tileSegment === "contacts") {
      return isAllDateFilter ? "Contacts" : "New";
    }
    return baseLabel;
  };

  const periods: ReportDatePreset[] = ["1W", "1M", "MTD", "YTD", "All"];

  const getDateRangeForPeriod = (period: ReportDatePreset): { start: Date | null; end: Date | null } => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    switch (period) {
      case "1W": {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        return { start, end };
      }
      case "1M": {
        const start = new Date(now);
        start.setMonth(start.getMonth() - 1);
        start.setHours(0, 0, 0, 0);
        return { start, end };
      }
      case "MTD": {
        const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        return { start, end };
      }
      case "YTD": {
        const start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
        return { start, end };
      }
      case "All":
        return { start: null, end: null };
    }
  };

  const formatPeriodDateRange = (period: ReportDatePreset): string => {
    if (period === "All") return "All";
    const { start, end } = getDateRangeForPeriod(period);
    if (start && end) {
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    }
    return period;
  };

  const handlePeriodClick = (period: ReportDatePreset) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        applyReportDateToSearchParams(next, { preset: period, customRange: undefined });
        return next;
      },
      { replace: true },
    );
    setCurrentPage(1);
  };

  const handleDateRangeSelect = (range: DayPickerRange | undefined) => {
    setTempDateRange(range);
  };

  const handleDone = () => {
    if (tempDateRange?.from && tempDateRange?.to) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          applyReportDateToSearchParams(next, { preset: "All", customRange: tempDateRange });
          return next;
        },
        { replace: true },
      );
    }
    setCurrentPage(1);
    setCalendarOpen(false);
  };

  const handleCancel = () => {
    setTempDateRange(customRange);
    setCalendarOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setCalendarOpen(open);
    if (open) {
      setTempDateRange(customRange);
    }
  };

  const formatDateRange = () => {
    if (customRange?.from && customRange?.to) {
      return `${customRange.from.toLocaleDateString()} - ${customRange.to.toLocaleDateString()}`;
    }
    return "Custom Range";
  };

  const handleSegmentClick = (next: ContactSegment) => {
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next === "contacts") {
          nextParams.delete("segment");
        } else {
          nextParams.set("segment", next);
        }
        return nextParams;
      },
      { replace: true },
    );
    setCurrentPage(1);
    setSelectedLeads(new Set());
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  const handleSelectLead = (leadId: string) => {
    setSelectedLeads((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  const handleSelectVisible = () => {
    if (isSelectAll) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(leads.map((lead) => lead._id)));
    }
  };

  const handleSelectAll = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      appendLeadsListFilters(params);
      params.append("idsOnly", "true");
      const url = `/api/leads${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await fetch(url);

      if (response.ok) {
        const data = await response.json();
        const allLeadIds = new Set<string>((data.leadIds || []).map((id: string) => String(id)));
        setSelectedLeads(allLeadIds);
      } else {
        console.error("Failed to fetch all lead IDs");
      }
    } catch (error) {
      console.error("Failed to fetch all lead IDs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToGroup = () => {
    if (selectedLeads.size === 0) return;
    setIsAddToGroupDialogOpen(true);
    setSelectedGroupId("");
  };

  const handleAddToGroupSubmit = async () => {
    if (!selectedGroupId || selectedLeads.size === 0) return;

    setIsAddingToGroup(true);
    try {
      const response = await fetch(`/api/lead-groups/${selectedGroupId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          leadIds: Array.from(selectedLeads),
        }),
      });

      if (response.ok) {
        setIsAddToGroupDialogOpen(false);
        setSelectedLeads(new Set());
        setSelectedGroupId("");
        // Optionally refresh the leads list
      } else {
        const data = await response.json();
        console.error("Failed to add leads to group:", data.error);
        alert(data.error || "Failed to add leads to group");
      }
    } catch (error) {
      console.error("Failed to add leads to group:", error);
      alert("An error occurred while adding leads to group");
    } finally {
      setIsAddingToGroup(false);
    }
  };

  const handleDeleteAll = async () => {
    if (selectedLeads.size === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedLeads.size} contact${selectedLeads.size !== 1 ? "s" : ""}? This action cannot be undone.`
    );

    if (!confirmed) return;

    setIsDeleting(true);
    try {
      const response = await fetch("/api/leads", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          leadIds: Array.from(selectedLeads),
        }),
      });

      if (response.ok) {
        setSelectedLeads(new Set());
        // Refresh the leads list
        setIsLoading(true);
        const params = new URLSearchParams();
        appendLeadsListFilters(params);
        params.append("page", currentPage.toString());
        params.append("limit", pageSize.toString());
        const url = `/api/leads${params.toString() ? `?${params.toString()}` : ""}`;
        const refreshResponse = await fetch(url);

        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          setLeads(leadsWithoutDeletedAt(data.leads || []));
          if (data.tileCounts) {
            setTileCounts(data.tileCounts);
          }
          if (data.pagination) {
            setTotalCount(data.pagination.totalCount || 0);
            setTotalPages(data.pagination.totalPages || 0);
          }
        }
      } else {
        const data = await response.json();
        console.error("Failed to delete leads:", data.error);
        alert(data.error || "Failed to delete leads");
      }
    } catch (error) {
      console.error("Failed to delete leads:", error);
      alert("An error occurred while deleting leads");
    } finally {
      setIsDeleting(false);
      setIsLoading(false);
    }
  };

  const handleCreateLead = async () => {
    setIsCreating(true);
    try {
      const response = await fetch("/api/leads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newLead),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewLead({
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
        });
        // Refresh the leads list
        setIsLoading(true);
        const params = new URLSearchParams();
        appendLeadsListFilters(params);
        params.append("page", currentPage.toString());
        params.append("limit", pageSize.toString());
        const url = `/api/leads${params.toString() ? `?${params.toString()}` : ""}`;
        const refreshResponse = await fetch(url);

        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          setLeads(leadsWithoutDeletedAt(data.leads || []));
          if (data.tileCounts) {
            setTileCounts(data.tileCounts);
          }
          if (data.pagination) {
            setTotalCount(data.pagination.totalCount || 0);
            setTotalPages(data.pagination.totalPages || 0);
          }
        }
      } else {
        const data = await response.json();
        console.error("Failed to create lead:", data.error);
        alert(data.error || "Failed to create lead");
      }
    } catch (error) {
      console.error("Failed to create lead:", error);
      alert("An error occurred while creating lead");
    } finally {
      setIsCreating(false);
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        {periods.map((period) => (
          <Button
            key={period}
            variant={preset === period && !customRange ? "default" : "outline"}
            size="sm"
            onClick={() => handlePeriodClick(period)}
          >
            {period}
          </Button>
        ))}
        <Popover open={calendarOpen} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant={customRange || (preset !== "All" && !customRange) ? "default" : "outline"}
              size="sm"
              className="gap-2"
            >
              <CalendarIcon className="h-4 w-4" />
              {customRange?.from && customRange?.to
                ? formatDateRange()
                : preset !== "All" && !customRange
                ? formatPeriodDateRange(preset)
                : "Custom Range"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={tempDateRange}
              onSelect={handleDateRangeSelect}
              numberOfMonths={1}
              className="rounded-md border-0"
            />
            <div className="flex justify-end gap-2 p-3 border-t">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleDone}
                disabled={!tempDateRange?.from || !tempDateRange?.to}
              >
                Done
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {CONTACT_TILES.map(({ segment: tileSegment, label }) => (
          <Card
            key={tileSegment}
            className={`cursor-pointer transition-colors ${
              segment === tileSegment ?
                "ring-2 ring-primary bg-accent/50"
              : "hover:bg-accent"
            }`}
            onClick={() => handleSegmentClick(tileSegment)}
          >
            <CardContent className="pt-6 pb-6">
              <div className="text-center">
                <p className="text-3xl font-bold tabular-nums">
                  {(tileCounts[tileSegment] ?? 0).toLocaleString()}
                </p>
                <p className="text-muted-foreground mt-2">
                  {getContactTileLabel(tileSegment, label)}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="flex gap-2 mb-4 items-center justify-between">
        <div className="flex gap-2 items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={selectedLeads.size === 0}>
                Actions
                <MoreHorizontal className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleAddToGroup} disabled={selectedLeads.size === 0}>
                Add to Group
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={handleDeleteAll} 
                disabled={selectedLeads.size === 0 || isDeleting}
                className="text-destructive focus:text-destructive"
              >
                Delete All
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <span className="text-sm text-muted-foreground">
            {selectedLeads.size} selected
          </span>
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={() => setIsCreateDialogOpen(true)}
        >
          Create
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <div className="flex items-center gap-1">
                <Checkbox
                  checked={isSelectAll}
                  onCheckedChange={handleSelectVisible}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-0.5 hover:bg-accent rounded-sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ChevronDown className="h-3 w-3 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={handleSelectVisible}>
                      Select Visible
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleSelectAll}>
                      Select All
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => handleSortColumn("name")}
            >
              <div className="flex items-center gap-1">
                Name
                {sortBy === "name" ? (
                  sortDir === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  )
                ) : (
                  <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground opacity-50 shrink-0" />
                )}
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => handleSortColumn("instagram")}
            >
              <div className="flex items-center gap-1">
                Instagram
                {sortBy === "instagram" ? (
                  sortDir === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  )
                ) : (
                  <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground opacity-50 shrink-0" />
                )}
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => handleSortColumn("email")}
            >
              <div className="flex items-center gap-1">
                Email
                {sortBy === "email" ? (
                  sortDir === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  )
                ) : (
                  <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground opacity-50 shrink-0" />
                )}
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => handleSortColumn("phone")}
            >
              <div className="flex items-center gap-1">
                Phone
                {sortBy === "phone" ? (
                  sortDir === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  )
                ) : (
                  <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground opacity-50 shrink-0" />
                )}
              </div>
            </TableHead>
            <TableHead
              className="text-right tabular-nums cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => handleSortColumn("amountSpent")}
            >
              <div className="flex items-center gap-1 justify-end">
                Amount Spent
                {sortBy === "amountSpent" ? (
                  sortDir === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  )
                ) : (
                  <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground opacity-50 shrink-0" />
                )}
              </div>
            </TableHead>
            <TableHead
              className="text-right tabular-nums cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => handleSortColumn("numPurchases")}
            >
              <div className="flex items-center gap-1 justify-end">
                # Purchases
                {sortBy === "numPurchases" ? (
                  sortDir === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  )
                ) : (
                  <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground opacity-50 shrink-0" />
                )}
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => handleSortColumn("lastActive")}
            >
              <div className="flex items-center gap-1">
                Last Active
                {sortBy === "lastActive" ? (
                  sortDir === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  )
                ) : (
                  <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground opacity-50 shrink-0" />
                )}
              </div>
            </TableHead>
            <TableHead
              className="text-right tabular-nums cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => handleSortColumn("numEngagements")}
            >
              <div className="flex items-center gap-1 justify-end">
                # Engagements
                {sortBy === "numEngagements" ? (
                  sortDir === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  )
                ) : (
                  <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground opacity-50 shrink-0" />
                )}
              </div>
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => handleSortColumn("joined")}
            >
              <div className="flex items-center gap-1">
                Joined
                {sortBy === "joined" ? (
                  sortDir === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  )
                ) : (
                  <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground opacity-50 shrink-0" />
                )}
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {leads && leads.length > 0 ? (
            leads.map((lead) => (
              <TableRow 
                key={lead._id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/contacts/${lead._id}/edit`)}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedLeads.has(lead._id)}
                    onCheckedChange={() => handleSelectLead(lead._id)}
                  />
                </TableCell>
                <TableCell>{getName(lead)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {(() => {
                    const handle = lead.instagram?.igUsername;
                    if (!handle) return "-";
                    const href = instagramProfileUrlFromHandle(handle);
                    if (!href) return "-";
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline underline-offset-4"
                      >
                        {handle}
                      </a>
                    );
                  })()}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {lead.email ? (
                    <a
                      href={`mailto:${encodeURIComponent(lead.email)}`}
                      className="text-primary underline underline-offset-4"
                    >
                      {lead.email}
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {lead.phoneNumber ? (
                    <Link
                      to={`/messages/sms/inbox/${encodeURIComponent(lead.phoneNumber)}`}
                      className="text-primary underline underline-offset-4"
                    >
                      {formatPhoneNumber(lead.phoneNumber)}
                    </Link>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatAmountSpent(lead.amountSpent)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatNumPurchases(lead.numPurchases)}
                </TableCell>
                <TableCell>
                  {lead.lastActive ? formatDate(lead.lastActive) : "-"}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatNumEngagements(lead.numEngagements)}
                </TableCell>
                <TableCell>{lead.sortDate ? formatDate(lead.sortDate) : "-"}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={10} className="h-24 text-center">
                No contacts found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={isAddToGroupDialogOpen} onOpenChange={setIsAddToGroupDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add to Group</DialogTitle>
            <DialogDescription>
              Select a group to add {selectedLeads.size} contact{selectedLeads.size !== 1 ? "s" : ""} to.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Group</label>
              <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent>
                  {leadGroups.map((group) => (
                    <SelectItem key={group._id} value={group._id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex-row gap-2 justify-center">
            <Button
              onClick={handleAddToGroupSubmit}
              disabled={!selectedGroupId || isAddingToGroup}
            >
              {isAddingToGroup ? "Adding..." : `Add ${selectedLeads.size} contact${selectedLeads.size !== 1 ? "s" : ""} to group`}
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsAddToGroupDialogOpen(false)}
              disabled={isAddingToGroup}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Contact</DialogTitle>
            <DialogDescription>
              Add a new contact to your list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={newLead.firstName}
                onChange={(e) => setNewLead({ ...newLead, firstName: e.target.value })}
                placeholder="Enter first name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={newLead.lastName}
                onChange={(e) => setNewLead({ ...newLead, lastName: e.target.value })}
                placeholder="Enter last name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={newLead.email}
                onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                placeholder="Enter email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <PhoneInput
                id="phoneNumber"
                defaultCountry="US"
                international={false}
                value={newLead.phoneNumber}
                onChange={(value) => setNewLead({ ...newLead, phoneNumber: value || "" })}
                className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
              />
            </div>
          </div>
          <DialogFooter className="flex-row gap-2 justify-center">
            <Button
              onClick={handleCreateLead}
              disabled={isCreating}
            >
              {isCreating ? "Creating..." : "Create Contact"}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                setNewLead({
                  firstName: "",
                  lastName: "",
                  email: "",
                  phoneNumber: "",
                });
              }}
              disabled={isCreating}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {(totalPages > 1 || totalCount > 0) && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
              <SelectTrigger size="sm" className="w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">
              {totalCount} contacts
            </span>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || isLoading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm">
                {currentPage} of {totalPages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || isLoading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

